<script setup>

import { ref } from 'vue';
import AppIcon from 'module/icon/AppIcon.vue';

defineProps({
    color: {
        type: String,
    },
    error: {
        type: Boolean,
    },
    hasAutocomplete: {
        type: Boolean,
    },
    autocompleteExpanded: {
        type: Boolean,
    },
    label: {
        type: String,
    },
    name: {
        type: String,
    },
    hideLabel: {
        type: Boolean,
    },
    id: {
        type: String,
    },
    type: {
        type: String,
    },
    event: {
        type: String,
    },
    min: {
        type: String,
    },
    max: {
        type: String,
    },
    step: {
        type: String,
    },
    pattern: {
        type: String,
    },
    inputMode: {
        type: String,
    },
    maxlength: {
        type: Number,
    },
    autofocus: {
        type: Boolean,
    },
    autocompleteId: {
        type: String,
    },
    iconLabel: {
        type: Boolean,
    },
    info: {
        type: Boolean,
    },
    disabled: {
        type: Boolean,
    },
    readonly: {
        type: Boolean,
    },
    infoVisible: {
        type: Boolean,
    },
    iconLabelRight: {
        type: Boolean,
    },
    flatRight: {
        type: Boolean,
    },
    hasActionIcon: {
        type: Boolean,
    },
    hasSelect: {
        type: Boolean,
    },
    infoSearchbar: {
        type: Boolean,
    },
    placeholder: {
        type: String,
    },
    value: {
        type: String,
    },
    size: {
        type: Number,
    },
    noFocus: {
        type: Boolean,
    },
    explicitValidationMessageOutlet: {
        type: Boolean,
    },
    actionIconAriaLabel: {
        type: String,
    },
});

const inputElement = ref(null);
function setFocus() {
    inputElement.value?.focus();
}

function select() {
    const minSelectionValue = 0;
    const maxSelectionValue = 99999;
    inputElement.value?.setSelectionRange(minSelectionValue, maxSelectionValue);
}

defineExpose({ setFocus, select });
</script>

<template>
    <div
        :class="[
            'm-input',
            { [`m-input--${color}`]: color },
            { 'm-input--error': error },
        ]"
        :role="hasAutocomplete ? 'combobox' : null"
        :aria-expanded="autocompleteExpanded ? 'true' : 'false'"
    >
        <label
            v-if="label"
            :for="name"
            :class="['m-input__label', { 'm-input__label--screenreaderOnly': hideLabel }]"
        >
            {{ label }}
        </label>
        <div class="m-input__group">
            <input
                :id="id || name"
                ref="inputElement"
                :name="name"
                :type="type"
                :data-event="event"
                :min="min"
                :max="max"
                :step="step"
                :pattern="pattern"
                :inputmode="inputMode"
                :maxlength="maxlength"
                :autofocus="autofocus"
                autocomplete="off"
                autocapitalize="off"
                autocorrect="off"
                spellcheck="false"
                aria-describedby="autocompleteExplainer"
                aria-autocomplete="list"
                role="searchbox"
                aria-activedescendant=""
                aria-haspopup="listbox"
                :aria-controls="autocompleteId"
                :class="[
                    'm-input__input',
                    { 'm-input__input--hasIconLabel': iconLabel },
                    { 'm-input__input--noIconLabel': !iconLabel },
                    { [`m-input__input--${color}`]: color },
                    { 'm-input__input--hasInfo': !!info },
                    { 'm-input__input--disabled': disabled },
                    { 'm-input__input--readonly': readonly },
                    { 'm-input__input--error': error },
                    { 'm-input__input--infoVisible': infoVisible },
                    { 'm-input__input--hasIconLabelRight': iconLabelRight },
                    { 'm-input__input--flatRight': flatRight },
                    { 'm-input__input--hasActionIcon': hasActionIcon },
                    { 'm-input__input--hasSelect': hasSelect },
                    { 'm-input__input--infoSearchbar': infoSearchbar },
                ]"
                :placeholder="placeholder"
                :value="value"
                :size="size"
                :disabled="disabled"
                :readonly="readonly"
                :data-qa="id || name"
                @focus="noFocus && (event => event.target.blur())"
            >
            <div
                v-if="iconLabel"
                class="m-input__iconLabel"
                :class="{
                    'm-input__iconLabel--error': error,
                    'm-input__iconLabel--iconLabelRight': iconLabelRight,
                }"
            >
                <AppIcon
                    name="link"
                    size="small"
                />
            </div>
            <button
                v-if="hasActionIcon"
                class="m-input__actionIcon"
                type="button"
                :aria-controls="id"
                :aria-label="actionIconAriaLabel"
            >
                <!-- TODO <ActionIconLabelComponent /> -->
            </button>
        </div>
        <div
            v-if="!explicitValidationMessageOutlet"
            class="m-input__validationMessageOutlet"
        >
            <!-- TODO <ValidationMessageComponent /> -->
        </div>
    </div>
</template>

<style lang="scss">
@use "styles";
</style>
